import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button, Field, Input, Link, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import axios from "axios";

// components
import { useLoginUser } from "../hooks/useAuthMutations";
import { useAuth } from "../../../provider/authProvider";
import { useNotify } from "../../../shared/components/SuperToast";
import { t } from "i18next";

import { useItemValidation } from "../../../shared/hooks/useItemValidation";
import Logo from "../../tenants/components/Logo";
import { useResendInvite } from "../../identity/hooks/useIdentityMutations";
import { useTenantBySlugQuery } from "../../tenants/hooks/useTenantQueries";
import TenantNotFound from "../../tenants/components/TenantNotFound";
import { credentialsSchema } from "@shared/schemas";

const apiUrl = process.env.REACT_APP_API_URL?.trim();
const turnstileSiteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();
const turnstileIframeBaseUrl = (process.env.REACT_APP_TURNSTILE_IFRAME_URL || "").trim();

const useStyles = makeStyles({
  button: { flex: 1 },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorNeutralBackground3,
    minHeight: "100vh",
    width: "100%",
  },
  form: {
    width: "400px",
    padding: "32px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: "8px",
    boxShadow: tokens.shadow16,
  },
  footer: {
    display: "flex",
    gap: "15px",
    justifyContent: "space-between",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    marginBottom: "24px",
  },
  logo: {
    width: "64px",
    height: "64px",
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
  },
});

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const styles = useStyles();
  const { setAccessToken, setUser } = useAuth();
  const { notify } = useNotify();

  const [form, setForm] = useState({ email: "", password: "", confirmationPassword: "" });
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [isTurnstileReady, setIsTurnstileReady] = useState(!turnstileSiteKey);
  const [turnstileError, setTurnstileError] = useState("");
  const slugParam = searchParams.get("slug")?.trim() || null;

  const turnstileIframeUrl = (() => {
    if (!turnstileSiteKey) return "";
    try {
      const url = new URL(turnstileIframeBaseUrl, window.location.origin);
      url.searchParams.set("sitekey", turnstileSiteKey);
      url.searchParams.set("parentOrigin", window.location.origin);
      return url.toString();
    } catch {
      return "";
    }
  })();

  const resolvedSubdomain = (() => {
    if (slugParam) return slugParam;
    const hostParts = window.location.hostname.split(".");
    return hostParts.length > 2 ? hostParts[0] : null;
  })();

  const { data: slugTenant, isLoading: isLoadingTenant } = useTenantBySlugQuery(resolvedSubdomain);

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateCredentials = useItemValidation(credentialsSchema);

  // Pre-fill email from URL parameter
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setForm((prev) => ({ ...prev, email: emailParam }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!turnstileSiteKey) return;
    if (!turnstileIframeUrl) {
      setIsTurnstileReady(false);
      setTurnstileError(t("nounPhrases.captchaIframeConfig", "CAPTCHA iframe URL is invalid."));
      return;
    }

    const expectedOrigin = new URL(turnstileIframeUrl).origin;

    setIsTurnstileReady(false);
    setTurnstileToken("");
    setTurnstileError("");

    const onMessage = (event) => {
      if (event.origin !== expectedOrigin) return;

      const payload = event.data;
      if (!payload || payload.source !== "itsm-turnstile") return;

      if (payload.type === "ready") {
        setIsTurnstileReady(true);
        setTurnstileError("");
        return;
      }

      if (payload.type === "token") {
        setIsTurnstileReady(true);
        setTurnstileToken(payload.token || "");
        setTurnstileError("");
        return;
      }

      if (payload.type === "expired") {
        setTurnstileToken("");
        return;
      }

      if (payload.type === "error") {
        setTurnstileToken("");
        setIsTurnstileReady(false);
        setTurnstileError(
          t(
            "nounPhrases.captchaLoadError",
            "CAPTCHA failed to load. Check ad-blockers, DNS filtering, or site key domain settings.",
          ) + (payload.errorCode ? ` (code: ${payload.errorCode})` : ""),
        );
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [turnstileIframeUrl]);

  // Fetch user from /users/me endpoint
  const fetchCurrentUser = async (accessToken) => {
    try {
      setIsLoadingUser(true);
      const { data } = await axios.get(`${apiUrl}/users/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      console.log("User data fetched:", data);
      setUser(data);
      notify(t("nounPhrases.authenticationSuccessful"), "success");

      // Redirect to original page unless it was the logout route; otherwise go home
      const from = location.state?.from?.pathname;
      const target = from && from !== "/logout" ? from : "/";
      navigate(target, { replace: true });
    } catch (error) {
      console.error("Failed to fetch user:", error);
    } finally {
      setIsLoadingUser(false);
    }
  };

  // Mutations
  const {
    mutate: loginUser,
    isPending: isPendingLoginUser,
    error: errorLoginUser,
    reset: resetLoginError,
  } = useLoginUser((data) => {
    console.log("Login successful, accessToken:", data.accessToken);
    setAccessToken(data.accessToken);
    // Refresh token is set as HTTP-only cookie by backend automatically
    fetchCurrentUser(data.accessToken);
  });

  const { mutate: resentInvite, isPending: isPendingResentInvite } = useResendInvite(
    () => {
      notify(t("nounPhrases.invitationResent", "Invitation resent successfully!"), "success");
      resetLoginError();
    },
    (error) =>
      notify(error.message || t("nounPhrases.invitationResendFailed", "Failed to resend invitation."), "error"),
  );

  // Handlers
  const handleChange = (field) => (e) => {
    const updated = { ...form, [field]: e.target.value };
    setForm(updated);
    const errors = validateCredentials(updated);
    delete errors.confirmationPassword;
    setValidationErrors(errors);
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    resetLoginError();

    const subdomain = resolvedSubdomain;

    if (turnstileSiteKey && !turnstileToken) {
      notify(t("nounPhrases.completeCaptcha", "Please complete the CAPTCHA."), "error");
      return;
    }

    // Send only required fields for login to satisfy backend validator
    loginUser({ email: form.email, password: form.password, subdomain, turnstileToken });
  };

  // Render TenantNotFound if tenant doesn't exist and loading is complete
  if (resolvedSubdomain && !isLoadingTenant && !slugTenant) {
    return <TenantNotFound />;
  }

  return (
    <div id="auth-page" className={styles.container}>
      <div className={styles.form}>
        <div className={styles.header}>
          <Logo logoUrl={isLoadingTenant ? undefined : slugTenant?.logo_path} alt="Logo" className={styles.logo} />
          <h1 className={styles.title}>{t("nounPhrases.signInToApp", { appName: slugTenant?.name || "easyITSM" })}</h1>
        </div>
        <form onSubmit={handleSubmit}>
          <Field
            label={t("generic.email")}
            orientation="vertical"
            required
            validationState={validationErrors.email ? "error" : "none"}
          >
            <Input id="email" type="email" value={form.email} onChange={handleChange("email")} required />
          </Field>
          <Field
            label={t("generic.password")}
            orientation="vertical"
            required
            validationState={validationErrors.password ? "error" : "none"}
          >
            <Input id="password" type="password" value={form.password} onChange={handleChange("password")} required />
          </Field>
          <div style={{ color: tokens.colorPaletteRedForeground1, margin: "10px 0" }}>
            {errorLoginUser && (
              <span>
                {errorLoginUser?.response?.data?.message ||
                  errorLoginUser?.response?.data?.error ||
                  errorLoginUser?.message ||
                  "An error occurred"}
                {errorLoginUser?.response?.data?.status === "invited" && (
                  <Link
                    style={{ marginLeft: "5px" }}
                    disabled={isPendingResentInvite}
                    onClick={() => resentInvite({ email: form.email, slug: resolvedSubdomain })}
                  >
                    {isPendingResentInvite ? "Resending..." : "Resend invitation"}
                  </Link>
                )}
              </span>
            )}
            {isLoadingUser && <span>Loading user...</span>}
          </div>
          <div style={{ textAlign: "right", marginBottom: "10px" }}>
            <span
              style={{
                color: tokens.colorBrandForeground1,
                cursor: "pointer",
                fontSize: "14px",
                textDecoration: "underline",
              }}
              onClick={() => navigate("/identity/reset-password")}
            >
              {t("nounPhrases.forgotPassword", "Forgot password?")}
            </span>
          </div>
          {turnstileSiteKey && (
            <>
              <div style={{ width: "100%" }}>
                <iframe
                  title="Cloudflare Turnstile"
                  src={turnstileIframeUrl}
                  style={{ width: "100%", height: "72px", border: "none", marginBottom: "8px" }}
                  scrolling="no"
                />
              </div>
              {!isTurnstileReady && (
                <div style={{ color: tokens.colorNeutralForeground3, fontSize: "12px", marginBottom: "10px" }}>
                  {t("nounPhrases.loadingCaptcha", "Loading CAPTCHA...")}
                </div>
              )}
              {!!turnstileError && (
                <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: "12px", marginBottom: "10px" }}>
                  {turnstileError}
                </div>
              )}
              {isTurnstileReady && !turnstileToken && !turnstileError && (
                <div style={{ color: tokens.colorPaletteRedForeground1, fontSize: "12px", marginBottom: "10px" }}>
                  {t("nounPhrases.completeCaptcha", "Please complete the CAPTCHA.")}
                </div>
              )}
            </>
          )}
          <div style={{ height: "30px" }} />
          <div className={styles.footer}>
            <Button
              className={styles.button}
              appearance="primary"
              type="submit"
              icon={isPendingLoginUser || isLoadingUser ? <Spinner size="tiny" /> : null}
              disabled={
                !form.email ||
                !form.password ||
                isPendingLoginUser ||
                isLoadingUser ||
                Object.keys(validationErrors).length > 0 ||
                (turnstileSiteKey && (!isTurnstileReady || !turnstileToken))
              }
            >
              {t("generic.login")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
